/**
 * Exam groups explorer — 10th Class ▸ Science ▸ Science exam.
 *
 *   const tree = useExamGroupTree({ api, exams });
 *   tree.nodes · tree.reload() · tree.byKey(key) → { kind, node, descendantIds }
 *
 * Groups are the SAME hierarchy Courses and the Question bank use, so "10th Class" is
 * written down once and means the same thing everywhere. Any depth of sub-group works;
 * the example is two levels because that is what a school usually needs.
 *
 * Node keys: group:<id> · exam:<id> · ungrouped
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Folder, FolderOpen, Exam, Tray } from '@phosphor-icons/react';

export const EXAM_TYPE_LABEL = {
  quiz: 'Quiz', exam: 'Exam', practice: 'Practice test', survey: 'Survey', feedback: 'Feedback',
};

export default function useExamGroupTree({ api, exams = [], enabled = true } = {}) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try { setGroups((await api.get('/categories')).data || []); }
    catch { setGroups((g) => g || []); }
    finally { setLoading(false); }
  }, [api, enabled]);
  useEffect(() => { reload(); }, [reload]);

  const nodes = useMemo(() => {
    const byParent = {};
    groups.forEach((g) => { (byParent[g.parent_id || '__root'] ||= []).push(g); });

    const examsFor = (groupId) => exams
      .filter((e) => (e.category_id || e.bank_category_id) === groupId)
      .sort((a, b) => a.title.localeCompare(b.title))
      .map((e) => ({
        key: `exam:${e.id}`, label: e.title, icon: Exam, kind: 'exam', data: e,
        iconClass: e.is_published ? 'text-violet-600' : 'text-muted-foreground',
        muted: !e.is_published,
        title: `${EXAM_TYPE_LABEL[e.assessment_type] || e.assessment_type}: ${e.title}${e.is_published ? '' : ' — draft'}`,
        badge: e.question_count || undefined,
        children: [],
      }));

    const build = (parentId) => (byParent[parentId] || [])
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((g) => {
        const children = [...build(g.id), ...examsFor(g.id)];
        return {
          key: `group:${g.id}`, label: g.name, kind: 'group', data: g,
          icon: children.length ? FolderOpen : Folder, iconClass: 'text-amber-500',
          title: g.path && g.path !== g.name ? g.path : g.description || undefined,
          // the whole subtree's exams, which is what selecting a group shows
          badge: g.total_exam_count || undefined,
          children,
        };
      });

    const out = build('__root');
    const loose = exams.filter((e) => !(e.category_id || e.bank_category_id));
    if (loose.length) {
      out.push({
        key: 'ungrouped', label: 'Not in a group', icon: Tray, iconClass: 'text-muted-foreground',
        kind: 'ungrouped', muted: true, badge: loose.length,
        children: loose.sort((a, b) => a.title.localeCompare(b.title)).map((e) => ({
          key: `exam:${e.id}`, label: e.title, icon: Exam, kind: 'exam', data: e,
          iconClass: e.is_published ? 'text-violet-600' : 'text-muted-foreground',
          muted: !e.is_published, children: [],
        })),
      });
    }
    return out;
  }, [groups, exams]);

  const index = useMemo(() => {
    const map = {};
    const walk = (list) => list.forEach((n) => { map[n.key] = n; walk(n.children || []); });
    walk(nodes);
    return map;
  }, [nodes]);

  const byKey = useCallback((key) => {
    const node = index[key];
    if (!node) return null;
    const descendants = [];
    const walk = (n) => (n.children || []).forEach((c) => { descendants.push(c); walk(c); });
    walk(node);
    return { kind: node.kind, node, descendants };
  }, [index]);

  return { nodes, groups, loading, reload, byKey };
}
